import React, {memo, useEffect, useState} from "react";
import {View, Text, StyleSheet, TouchableWithoutFeedback, FlatList/*, ActivityIndicator*/} from "react-native";
import {Button, VStack, Image, HStack, Box, Icon} from "native-base";
import {Ionicons} from '@expo/vector-icons';
import { useNavigation } from "@react-navigation/native";
import DashedLine from "react-native-dashed-line";
import {Cart, mapToItems, tagOptions} from "./lib/model";
import {PALETTE} from "./lib/palette";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {cartState} from "./state/cartState";
import apps, {SaleMode} from "./lib/apps";
import {money, holidaySurchargeAmount, payableAmount} from "./lib/utils";
import ThumbImage from "./component/ThumbImage";
import {useScrollMore, ScrollMoreHint} from "./component/ScrollMore";
import {BackButton, CIRCLE_BTN} from "./component/BackButton";
import {orderState} from "./state/orderState";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Order, OrderStatusRes} from "./lib/order_model";
import * as remote from "./lib/remote";
import safeStorage from "./lib/storage";
import {saveLastOrder} from "./lib/lastOrder";

function Title({ isCardPayment }) {
    const navigation = useNavigation<any>();
    const [cart, setCart] = useRecoilState(cartState);

    return (
        <HStack style={styles.header}>
            <BackButton
                onPress={() => {
                    setCart(new Cart(apps.getHappyhour().saleMode, apps.currentShop!!))
                    apps.getOrdIdAndClear();
                    apps.getOrdNoAndClear();
                    navigation.navigate('Thankyou');
                }}
                containerStyle={CIRCLE_BTN}
                size={12}
                thickness={2.5}
                hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
                blink={false}
            />
            <Text style={styles.headerTitle}>{isCardPayment ? 'Receipt' : 'Ordered'}</Text>
            <Box style={styles.headerBack}/>
        </HStack>
    );
}

// 콤팩트 헤더: 고정 큰 블록(이미지 176 + 텍스트 120 + 번호 90 ...) 대신 자연 높이 + 여백으로 압축.
// thanks 이미지는 작게 유지(158→90). 전체가 FlatList 헤더로 들어가 화면과 함께 스크롤됨.
function Thanks({ordId, ordNo, isCardPayment, orderStatus}) {
    const accent = apps.getAccent();
    const tint = apps.accentTint();
    const yourOrder = useRecoilValue(orderState);
    const [displayTime, setDisplayTime] = useState("");

    useEffect(() => {
        const now = new Date();
        // 일자 + 시각(초 제거). 손님 기기가 매장 현지 시간대이므로 기기 시각으로 충분.
        const d = now.toLocaleDateString(undefined, {weekday: "short", day: "numeric", month: "short", year: "numeric"});
        const t = now.toLocaleTimeString(undefined, {hour: "numeric", minute: "2-digit"});
        setDisplayTime(`${d} · ${t}`);
    }, []);

    const orderedMsg = (apps.currentShop != null && apps.currentShop.orderedMsg != null && apps.currentShop.orderedMsg.trim() !== "")
        ? apps.currentShop.orderedMsg : "Pay at the counter";
    const showKiyoNote = (apps.currentShop != null && apps.currentShop.shopid == "sushikiyocpr" && apps.currentShop.tblNo.startsWith("Q"));

    return (
        <VStack style={styles.thanksWrap}>
            <Image source={require("../assets/thanks.png")} alt="thanks" w={205} h={90} resizeMode="contain" />
            <Text style={[styles.thanksTitle, { color: accent }]}>THANKS FOR ORDERING</Text>
            <Text style={styles.thanksInfo}>{yourOrder.getOrderInfoLong()}</Text>

            <Box style={[styles.ordNumCard, { backgroundColor: tint, borderColor: PALETTE.BORDER }]}>
                <Text style={styles.ordNoLabel}>Order Number</Text>
                {ordNo > 0 ? (
                    <Text style={[styles.ordNo, { color: accent }]}>{ordNo}</Text>
                ) : (orderStatus === "REJECTED" || orderStatus === "TIMEOUT" || orderStatus === "FAILED") ? (
                    <Text style={[styles.ordNoError, { color: accent }]}>
                        {orderStatus === "REJECTED" ? "Order was rejected.\nPlease contact staff." :
                         orderStatus === "TIMEOUT"  ? "Order timed out.\nPlease contact staff." :
                                                      "Order failed.\nPlease contact staff."}
                    </Text>
                ) : (
                    orderStatus === "WAITING_PAYMENT" ? (
                        <Text style={styles.ordNoPending}>Payment received · assigning order ...</Text>
                    ) : null
                )}
                <Text style={styles.ordTime}>{displayTime} · {ordId}</Text>
            </Box>

            {!isCardPayment && (
                <Text style={[styles.payMsg, { color: accent }]}>{orderedMsg}</Text>
            )}
            {showKiyoNote && (
                <Text style={[styles.kiyoMsg, { color: accent }]}>Please pay at the counter first after placing your order</Text>
            )}
            <Image source={require("../assets/paperbottom.png")} alt="pb" h="18px" w="100%" style={{ marginTop: 8 }} />
        </VStack>
    );
}

function Total({ subtotal, holidayAmt, fee, total, showFee }) {
    // holidayAmt 는 formatted 문자열("$0.00")로 넘어오므로 표시 여부는 숫자로 되돌려 판정한다
    // (fee/showFee 와 동일한 방식 — 아래 ListFooterComponent 참조).
    const showHoliday = Number(String(holidayAmt).replace(/[^0-9.-]/g, "")) > 0;
    return (
        <VStack style={styles.totalContainer}>
            <HStack style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalPrice}>{subtotal}</Text>
            </HStack>
            {/* 공휴일 추가요금 — 결제수단 무관 표시(카운터결제도 카운터에서 청구). checkout.tsx와 동일 라벨·순서. */}
            {showHoliday && (
                <HStack style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Holiday Surcharge</Text>
                    <Text style={styles.totalPrice}>{holidayAmt}</Text>
                </HStack>
            )}
            {/* 수수료 0(카운터 결제/무수수료)이면 행 숨김 — checkout.tsx와 동일 정책 */}
            {showFee && (
                <HStack style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Processing Fee</Text>
                    <Text style={styles.totalPrice}>{fee}</Text>
                </HStack>
            )}
            <HStack style={styles.totalRowLast}>
                <Text style={styles.totalLabel}>Total</Text>
                <Text style={styles.totalPrice}>{total}</Text>
            </HStack>
        </VStack>
    );
}

    function Ordered({navigation}) {
        const accent = apps.getAccent();
        const cart = useRecoilValue(cartState);
        const [localOrdId] = useState(() => apps.ordId);
        // 항목 소계 스냅샷(마운트 시 1회) — Title의 back 버튼이 cart를 새로 비운 Cart로 교체하므로
        // cart.total()을 그때그때 읽으면 0이 될 수 있다(localOrdId와 동일한 스냅샷 패턴).
        const [pureSubtotal] = useState(() => cart.total());
        const holidayPct = apps.currentShop?.holidaySurcharge ?? 0;
        const [ordNo, setOrdNo] = useState(0);
        const [ordId, setOrdId] = useState(0);
        const [subtotal, setSubtotal] = useState(money(pureSubtotal));
        const [holidayAmt, setHolidayAmt] = useState(money(holidaySurchargeAmount(pureSubtotal, holidayPct)));
        const [fee, setFee] = useState(money(0));
        const [total, setTotal] = useState(money(payableAmount(pureSubtotal, holidayPct)));
        const [isCardPayment, setIsCardPayment] = useState(false);


        //get OrdNo
        // Fetch Order Status using useQuery
        const { data, error, isLoading } = useQuery({
            queryKey: ["orderStatus"],
            queryFn: () => remote.getOrderStatus(localOrdId),
            enabled: localOrdId > 0,
            retry: false,  // Disable default retry logic
            refetchInterval: (data) => (data?.ordNo > 0 ? false : 2000), // Retry every 2 seconds
            onSuccess: (data) => {
                setOrdNo(data.ordNo);
                setOrdId(data.orderId);
                console.log("data.orderId: ", data.orderId, ", data.ordNo: ", data.ordNo);
                // 주문 확정(ordNo>0 = txId 배정됨) 시 다시보기용 스냅샷 저장(orderId만).
                // 서버가 orderId로 txId를 해석해 세션 목록을 반환한다.
                if (data.ordNo > 0 && localOrdId > 0) {
                    const uuid = safeStorage.getItem("uuid") ?? "";
                    saveLastOrder(uuid, {
                        orderId: localOrdId,
                        shopId: apps.shopId,
                        tableNo: apps.tableNo,
                        placedAt: Date.now(),
                    });
                }
            },
        });

        const receipt = useMutation((orderId: number) => remote.findReceiptByOrderId(orderId), {
            onSuccess: (res) => {
                if (res && Object.keys(res).length > 0) {
                     // res.originalAmount = receipt.original_amount = 소계 + 공휴일 서차지(Important 2와 동일 유도,
                     // OrderReview.tsx 참조). pureSubtotal(항목 소계 스냅샷)과의 차이가 서차지다.
                     // 부동소수 뺄셈 금지 — 센트 정수로 계산. 음수/0(요율 변경 등으로 어긋난 구세션)이면 표시하지 않는다.
                     const originalCents = Math.round(Number(String(res.originalAmount).replace(/[^0-9.-]/g, "")) * 100);
                     const pureCents = Math.round(pureSubtotal * 100);
                     const holidayCents = originalCents - pureCents;
                     setSubtotal(money(pureSubtotal));
                     setHolidayAmt(money(holidayCents > 0 ? holidayCents / 100 : 0));
                     setFee(res.surchargeAmount);
                     setTotal(res.total);
                     setIsCardPayment(true);
                }

                //setOrdNo(apps.getOrdNoAndClear());
                //setOrdId(apps.getOrdIdAndClear());

            },
            onError: (err) => {
                // Handle error
            },
            onMutate: (orderId) => {
                // On mutate logic if needed
            },
        });

        // 마운트 1회: receipt 로드 + ord id 클리어
        useEffect(() => {
            if (localOrdId) {
                receipt.mutate(localOrdId);
            }
            apps.ordIdClear();
            apps.ordNoClear();
        }, []);

        // 10초 후 Thankyou로 자동 전환(idle 시 세션 초기화용). 단, Order Number(ordNo)가 도달한 뒤에야
        // 카운트다운을 시작한다 — 카드 결제는 ordNo가 몇 초 늦게 오므로, 마운트부터 세면 버튼이 뜨자마자
        // 넘어가 놓칠 수 있다. ordNo 도달 후부터 세면 View Order 버튼이 뜬 뒤 항상 온전한 10초를 확보한다.
        // 타이머는 ref에 보관해 View Order 핸들러에서도 정지 가능.
        const idleTimerRef = React.useRef(null);
        useEffect(() => {
            if (ordNo <= 0) return;             // Order Number 도달 전엔 카운트다운 시작 안 함
            if (idleTimerRef.current) return;   // 이미 시작했으면 재시작 안 함
            const targetTime = Date.now() + 10000;
            idleTimerRef.current = setInterval(() => {
                if (Date.now() >= targetTime) {
                    clearInterval(idleTimerRef.current);
                    idleTimerRef.current = null;
                    apps.ordIdClear();
                    apps.ordNoClear();
                    navigation.navigate('Thankyou');
                }
            }, 1000);
            return () => { if (idleTimerRef.current) { clearInterval(idleTimerRef.current); idleTimerRef.current = null; } };
        }, [ordNo]);

        // View Order: 사용자가 능동적으로 주문을 확인한 것이므로, 자동전환 타이머를 정지하고
        // 세션을 즉시 초기화(Thankyou와 동일: reload 재주문 방지 위해 URL 파라미터 제거 + ord id 클리어)한 뒤
        // 주문내역으로 이동한다. 이후 Thankyou로 강제 이동하지 않아 주문을 원하는 만큼 볼 수 있다.
        const onViewOrder = () => {
            if (idleTimerRef.current) { clearInterval(idleTimerRef.current); idleTimerRef.current = null; }
            if (typeof window !== "undefined" && window.history?.replaceState) {
                window.history.replaceState({}, '', '.');
            }
            apps.ordIdClear();
            apps.ordNoClear();
            navigation.navigate("OrderReview", {shopId: apps.shopId, orderId: localOrdId});
        };

        //console.log("ordNo: " + ordNo);
        const {showHint, scrollToEnd, scrollProps} = useScrollMore();   // 스크롤 "더 있음" 힌트

        // 전체를 하나의 FlatList로: 헤더(Thanks + View Order + Your items 라벨) → 아이템 → 합계(Footer).
        // 바깥 고정 블록이 없어 화면 전체가 스크롤되고, Your items/합계에 항상 도달 가능.
        return (
            <VStack style={{ flex: 1, backgroundColor: "white" }}>
                <Title isCardPayment={isCardPayment} />
                <FlatList
                    {...scrollProps}
                    style={{ flex: 1 }}
                    data={mapToItems(cart.items)}
                    scrollEnabled={true}
                    contentContainerStyle={{ paddingBottom: 28 }}
                    ListHeaderComponent={
                        <>
                            <Thanks ordId={ordId} ordNo={ordNo} isCardPayment={isCardPayment} orderStatus={data?.orderStatus} />
                            {ordNo > 0 && localOrdId > 0 && (
                                <TouchableWithoutFeedback onPress={onViewOrder}>
                                    <Box style={[styles.viewOrderBtn, { borderColor: accent }]}>
                                        <Text style={[styles.viewOrderText, { color: accent }]}>View Order</Text>
                                    </Box>
                                </TouchableWithoutFeedback>
                            )}
                            <Box style={styles.yourItemsHeader}>
                                <Text style={styles.yourItemsLabel}>Your Order</Text>
                            </Box>
                        </>
                    }
                    ListFooterComponent={<Total subtotal={subtotal} holidayAmt={holidayAmt} fee={fee} total={total}
                        showFee={isCardPayment && Number(String(fee).replace(/[^0-9.-]/g, "")) > 0} />}
                    renderItem={({ item, index }) => {
                        const isLast = index === cart.items.length - 1;
                        return (
                            <VStack style={styles.itemRowWrap}>
                                <HStack style={styles.itemList}>
                                    <ThumbImage shopid={apps.currentShop?.shopid ?? ""} menuid={item.menuid} imageVer={item.imageVer} style={styles.itemThumb} />
                                    <VStack style={styles.itemLeft}>
                                        <Text style={styles.itemName}>{item.name}</Text>
                                        <Text style={styles.itemOptions}>{item.optionsDescr()}</Text>
                                        {item.note ? <Text style={styles.itemNote}>📝 {item.note}</Text> : null}
                                    </VStack>
                                    <VStack style={styles.itemRight}>
                                        <Text style={styles.itemPrice}>{money(item.total)}</Text>
                                        <View style={styles.itemQtyChip}><Text style={styles.itemQtyChipText}>×{item.qty}</Text></View>
                                    </VStack>
                                </HStack>
                                {!isLast ?
                                    // 아래 Total 구분선과 톤을 맞춘 가는 점선
                                    <DashedLine dashLength={3} dashGap={3} dashThickness={1} dashColor={PALETTE.DIVIDER} style={{ height: 1 }} /> : null
                                }
                            </VStack>
                        );
                    }}
                />
                <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
            </VStack>
        )
    }


const styles = StyleSheet.create({
    viewOrderBtn: {
        alignSelf: "center",
        marginVertical: 10,
        paddingVertical: 8,
        paddingHorizontal: 24,
        borderRadius: PALETTE.RADIUS_PILL,
        borderWidth: 1.5,
        borderColor: PALETTE.ACCENT,
    },
    viewOrderText: {
        color: PALETTE.ACCENT,
        fontSize: 15,
        fontFamily: "NotoSans-Black",
        fontWeight: "900",
        letterSpacing: -0.3,
    },
    titleContainer: {
        height: 49,
        backgroundColor: "white",
        justifyContent: "flex-start",
        alignItems: "center",
        marginLeft: 20,
    }, title: {
        fontSize: 22,
        fontFamily: "NotoSans-Black",
        fontWeight: "900",
        color: PALETTE.TEXT,
        letterSpacing: -0.55,
        lineHeight: 35,
    }, titleBack: {
        height: 26, width: 26,
        marginTop: 5, marginRight: 10,
    },
    // 통일 헤더(OrderReview/checkout와 동일): back | 가운데 타이틀 | 스페이서
    header: {
        width: "100%",
        height: 56, alignItems: "center", justifyContent: "space-between",
        paddingHorizontal: 14, backgroundColor: "#fff",
        borderBottomWidth: 1, borderBottomColor: PALETTE.BORDER,
    }, headerBack: {
        width: 26, height: 26,
    }, headerTitle: {
        fontSize: 18, fontFamily: "NotoSans-Black", fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.4,
    },

    // --- Thanks(콤팩트 헤더) ---
    thanksWrap: {
        backgroundColor: PALETTE.BG,
        borderTopColor: PALETTE.BORDER, borderTopWidth: 1,
        alignItems: "center",
        paddingTop: 10,
    },
    thanksTitle: {
        fontSize: 18, fontFamily: "NotoSans-Black", color: PALETTE.ACCENT, fontWeight: "900",
        letterSpacing: -0.6, textAlign: "center", marginTop: 2,
    },
    thanksInfo: {
        color: PALETTE.MUTED, fontSize: 14, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.4, marginTop: 2,
    },
    // accent-tint number card (D10) — backgroundColor/borderColor applied inline at render time
    ordNumCard: {
        alignSelf: "stretch",
        marginHorizontal: 16,
        marginTop: 10,
        marginBottom: 4,
        borderRadius: PALETTE.RADIUS_MD,
        borderWidth: 1,
        padding: 16,
        alignItems: "center",
    },
    ordNoLabel: {
        color: PALETTE.MUTED, fontSize: 13, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.4,
    },
    ordNo: {
        fontSize: 42, fontFamily: "NotoSans-Black", color: PALETTE.ACCENT, fontWeight: "900", letterSpacing: -0.75, lineHeight: 48,
    },
    ordNoError: {
        color: PALETTE.ACCENT, fontSize: 14, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.3, marginTop: 2, textAlign: "center",
    },
    ordNoPending: {
        color: PALETTE.MUTED, fontSize: 12, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.3, marginTop: 2, textAlign: "center",
    },
    ordTime: {
        color: PALETTE.MUTED, fontSize: 12, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: 1, marginTop: 2,
    },
    payMsg: {
        color: PALETTE.ACCENT, fontSize: 18, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.4,
        textAlign: "center", marginTop: 8, paddingHorizontal: 16,
    },
    kiyoMsg: {
        color: PALETTE.ACCENT, fontSize: 15, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.4,
        textAlign: "center", marginTop: 6, paddingHorizontal: 16,
    },

    // --- Your items / 목록 ---
    yourItemsHeader: {
        height: 40,
        justifyContent: "center",
        borderBottomColor: PALETTE.TEXT,
        borderBottomWidth: 1,
        marginHorizontal: 20,
        marginTop: 15,
    },
    yourItemsLabel: {
        fontSize: 16, fontFamily: "NotoSans-Black", fontWeight: "900", color: PALETTE.TEXT,
    },
    itemRowWrap: {
        marginHorizontal: 20, backgroundColor: "white",
    },
    itemList: {
        width: "100%", backgroundColor: "white", minHeight: 72,
        paddingTop: 14, paddingBottom: 8, alignItems: "center",   // 상단 패딩 확대로 위 구분선과 간격 확보(하단은 수량 칩이 아래 구분선에 닿지 않게). minHeight: note 있으면 늘어남.
    }, itemThumb: {
        width: 40, height: 40, borderRadius: PALETTE.RADIUS_SM, marginRight: 10, backgroundColor: PALETTE.BORDER,
    }, itemLeft: {
        flex: 1, justifyContent: "center",
    }, itemName: {
        fontSize: 14,
        fontFamily: "NotoSans-Bold",
        fontWeight: "bold",
        letterSpacing: -0.3,
        color: PALETTE.TEXT,
    }, itemOptions: {
        fontSize: 12,
        fontFamily: "NotoSans-Regular",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
    }, itemNote: {
        fontSize: 11,
        fontFamily: "NotoSans-Regular",
        fontStyle: "italic",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
        marginTop: 2,
    }, itemRight: {
        justifyContent: "flex-start", alignItems: "flex-end",
    }, itemPrice: {
        fontSize: 16,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    }, itemQtyChip: {
        marginTop: 4,
        borderRadius: PALETTE.RADIUS_PILL,
        paddingHorizontal: 8,
        paddingVertical: 1,
        backgroundColor: PALETTE.QTY_CHIP,
    }, itemQtyChipText: {
        fontSize: 11,
        fontFamily: "NotoSans-Bold",
        fontWeight: "700",
        color: "#FFFFFF",
        letterSpacing: -0.2,
    },
    totalContainer: {
        backgroundColor: "white",
        marginHorizontal: 20,
    }, totalRow: {
        height: 39, width: "100%",
        borderTopWidth: 1, borderTopColor: PALETTE.DIVIDER, alignItems: "center",
    }, totalLabel: {
        flex: 1, justifyContent: "center",
        fontSize: 16, fontFamily: "NotoSans-Black", fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.4,
    }, totalPrice: {
        justifyContent: "center", alignItems: "flex-end", fontSize: 16, fontFamily: "NotoSans-Regular", letterSpacing: -0.3,
    }, totalRowLast: {
        height: 39, width: "100%",
        borderTopWidth: 1, borderTopColor: PALETTE.DIVIDER, alignItems: "center",
        borderBottomWidth: 1, borderBottomColor: PALETTE.DIVIDER,
    },

});

export default Ordered;
